import React, { useMemo } from 'react';
import PropTypes from 'prop-types';
import ReactTable from 'react-table';
import cx from 'classnames';
import { range } from 'lodash';
import { fullName } from '../../../common/helpers/users';
import Switch from '../../../common/Switch';
import Tooltip from '../../../common/tooltip';
import SelectInput from '../../../common/SelectInput';
import SimpleCurrencyInput from '../../../common/SimpleCurrencyInput';
import BarberAvatar from '../../barbers/components/BarberAvatar';
import Checkbox from '../../../common/Checkbox';
import { getMinuteOptionsForMinInterval } from '../../selectors';
import durationInitialFromService from '../utils/durationInitialFromService';
import CheckboxSpinner from './CheckboxSpinner';
import Icon, { GLYPHS } from '../../../common/icon';
import { t } from '../../../../modules/infra/i18n';

const EXPAND_SHOP_CLASS = 'js-expand-shop';

const currencyInputDefaults = {
  className: 'c-input c-input--prepend',
  type: 'text',
  placeholder: '0',
  precision: 2,
  separator: '.',
  delimiter: ',',
  addPrefix: true,
};

const Assignments = (props) => {
  const {
    onRowSelectionChange,
    onFieldChange,
    shops,
    expandedShopId,
    toggleExpandedShop,
    getValue,
    isSelected,
    isLoading,
    roleIdType,
    barbersByShop,
    isLoadingBarbersByShop,
  } = props;

  const getRowSelectionChangeHandler = (row, isShopRow) => (event) => {
    if (roleIdType.type === 'shop' && isShopRow) {
      return;
    }

    onRowSelectionChange(row.original, event);
  };

  const onCostInputBlur = (initialValue, row) => {
    const isNotChanged = typeof row.rawCost === 'undefined' || initialValue === row.rawCost;

    if (isNotChanged) {
      return;
    }

    onFieldChange(row.original, 'cost', row.rawCost);
  };

  const columns = [
    {
      id: 'expander',
      accessor: 'expandState',
      width: 50,
      maxSize: 50,
      sortable: false,
      Header: '',
      Cell: (row) => {
        const { shopId } = row.original;
        const isShopRow = !shopId;
        
        if (!isShopRow) {
          return null;
        }

        const isExpanded = expandedShopId === row.original.id;
        return (
          <div className={cx('c-expander-btn', EXPAND_SHOP_CLASS, { expanded : isExpanded })}>
            <Icon glyph={GLYPHS.arrow} className={EXPAND_SHOP_CLASS} />
          </div>
        );
      },
    },
    {
      accessor: 'name',
      minWidth: 308,
      sortable: false,
      Header: t('services:assignments.table.headers.location'),
      Cell: (row) => {
        const { alias, name, shopId } = row.original;
        const isShopRow = !shopId;
        const loading = isLoading(row.original);
        return (
          <React.Fragment>
            <div className={cx('u--mr--', { 'u-ml+': !isShopRow })}>
              {loading ? (
                <CheckboxSpinner />
              ) : (
                <Checkbox
                  checked={isSelected(row.original)}
                  onChange={getRowSelectionChangeHandler(row, isShopRow)}
                />
              )}
            </div>
            {!isShopRow && (
              <span className="c-avatar u-mr--">
                <BarberAvatar barber={row.original} />
              </span>
            )}
            <span className={cx('u-type--semibold', EXPAND_SHOP_CLASS)}>
              {isShopRow ? (alias || name) : fullName(row.original)}
            </span>
          </React.Fragment>
        );
      },
    },
    {
      accessor: 'duration',
      width: 262,
      sortable: false,
      Header: t('services:assignments.table.headers.duration'),
      Cell: (row) => {
        const { minInterval } = row.original;
        const { minuteOptions } = getMinuteOptionsForMinInterval(minInterval);
        const duration = getValue(row.original, 'duration');
        const {
          durationHour,
          durationMinute,
        } = durationInitialFromService({ duration });
        return (
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(2, 1fr)' }} className="u-width-100">
            <SelectInput
              className="c-select u-width-100"
              style={{ minWidth: 0 }}
              options={range(0, 13)}
              suffix={t('services:assignments.table.duration.hours')}
              disabled={!isSelected(row.original) || isLoading(row.original)}
              value={durationHour}
              onChange={(event) => {
                onFieldChange(row.original, 'duration', (event.target.value * 60) + durationMinute);
              }}
            />
            <SelectInput
              className="c-select u-width-100"
              style={{ minWidth: 0 }}
              options={minuteOptions}
              suffix={t('services:assignments.table.duration.minutes')}
              disabled={!isSelected(row.original) || isLoading(row.original)}
              value={durationMinute}
              onChange={(event) => {
                onFieldChange(row.original, 'duration', (durationHour * 60) + Number(event.target.value));
              }}
            />
          </div>
        );
      },
    },
    {
      accessor: 'price',
      width: 142,
      sortable: false,
      Header: t('services:assignments.table.headers.pricing'),
      Cell: (row) => {
        const value = getValue(row.original, 'cost');
        return (
          <SimpleCurrencyInput
            {...currencyInputDefaults}
            disabled={!isSelected(row.original) || isLoading(row.original)}
            value={value}
            onInputChange={(cents) => {
              // onInputBlur doesn't provide a raw value in cents, only formatted input value
              // at the same time, we don't need to re-render when value changes
              // so we just put the latest raw value to the table row object so we can use it later in onInputBlur
              row.rawCost = cents;
            }}
            onInputBlur={() => onCostInputBlur(value, row)}
          />
        );
      },
    },
    {
      accessor: 'enabled',
      width: 164,
      sortable: false,
      Header: <React.Fragment>
        {t('services:assignments.table.headers.online_visibility')}
        <Tooltip className="u-type--no-transform c-info-tooltip-new--form">
          <div className="u-m--">
            {t('services:assignments.table.headers.online_visibility_tooltip')}
          </div>
        </Tooltip>
      </React.Fragment>,
      Cell: (row) => {
        const isBarberRow = !!row.original.shopId;
        const isBarberVisibleOnline = row.original.enabled;
        return (
          <React.Fragment>
            <Switch
              checked={!!getValue(row.original, 'enabled')}
              disabled={!isSelected(row.original) || isLoading(row.original)}
              onChange={checked => onFieldChange(row.original, 'enabled', checked)}
            />
            <Tooltip
              className="u-type--no-transform c-info-tooltip-new--form"
              display-if={isBarberRow && !isBarberVisibleOnline}
            >
              <div className="u-m--">
                {t('services:assignments.table.headers.staff_not_visible_tooltip')}
              </div>
            </Tooltip>
          </React.Fragment>
        );
      },
    },
    {
      accessor: 'prepaid',
      width: 166,
      sortable: false,
      Header: <React.Fragment>
        {t('services:assignments.table.headers.requires_prepaid')}
        <Tooltip className="u-type--no-transform c-info-tooltip-new--form">
          <div className="u-m--">
            {t('services:assignments.table.headers.requires_prepaid_tooltip')}
          </div>
        </Tooltip>
      </React.Fragment>,
      Cell: row => (
        <Switch
          checked={!!getValue(row.original, 'requiresPrepaid')}
          disabled={!isSelected(row.original) || isLoading(row.original)}
          onChange={checked => onFieldChange(row.original, 'requiresPrepaid', checked)}
        />
      ),
    },
    {
      accessor: 'kiosk',
      width: 103,
      sortable: false,
      Header: <React.Fragment>
        {t('services:assignments.table.headers.kiosk')}
        <Tooltip className="u-type--no-transform c-info-tooltip-new--form">
          <div className="u-m--">
            {t('services:assignments.table.headers.kiosk_tooltip')}
          </div>
        </Tooltip>
      </React.Fragment>,
      Cell: (row) => {
        const { shopId } = row.original;

        if (shopId) {
          return '';
        }

        return (
          <Switch
            checked={!!getValue(row.original, 'kioskEnabled')}
            disabled={!isSelected(row.original) || isLoading(row.original)}
            onChange={checked => onFieldChange(row.original, 'kioskEnabled', checked)}
          />
        );
      },
    },
  ];

  const data = useMemo(() => {
    const resolvedData = [];

    shops.forEach((shopItem) => {
      resolvedData.push(shopItem);

      // Only show barbers if shop is expanded
      if (expandedShopId === shopItem.id) {
        if (isLoadingBarbersByShop[shopItem.id]) {
          resolvedData.push({
            id: 'loading',
            shopId: shopItem.id,
            isLoadingRow: true,
          });
        } else {
          const shopBarbers = barbersByShop[shopItem.id] || [];
          const preparedShopBarbers = shopBarbers
            .sort((a, b) => a.order - b.order)
            .map((barber) => ({ ...barber, minInterval: shopItem.minInterval }));

          resolvedData.push(...preparedShopBarbers);
        }
      }
    });
    return resolvedData;
  }, [shops, barbersByShop, isLoadingBarbersByShop, expandedShopId]);

  return (
    <div
      className="u-max-width-1000 c-service-assignments-table c-wrapper-allow-header"
    >
      <ReactTable
        className="c-table-card c-table-new c-overflow-visible c-header-overflow-visible"
        minRows={0}
        showPagination={false}
        data={data}
        columns={columns.map(column => ({
          ...column,
          Cell: (row) => {
            if (row.original.isLoadingRow) {
              return column.accessor === 'name' ? <div className="c-loader__spin c-loader__spin--small" /> : null;
            }
            return column.Cell(row);
          }
        }))}
        getTdProps={(_, row) => ({
          className: cx({ [EXPAND_SHOP_CLASS]: !!row.original.barbers }),
        })}
        getTrGroupProps={(_, row) => {
          const { barbers, id } = row.original;

          if (!barbers) {
            return {
              className: 'c-barber-row c-no-margin',
            };
          }

          const onClick = (e) => {
            if (e.target.classList.contains(EXPAND_SHOP_CLASS)) {
              toggleExpandedShop(id);
            }
          };

          const shopHasLoadedBarbers = barbersByShop[id] && barbersByShop[id].length > 0;
          const shopBarbersAreLoading = isLoadingBarbersByShop[id];

          return {
            className: cx('c-shop-row', {
              'c-shop-row--expanded': expandedShopId === id,
              'c-shop-row--empty': !shopHasLoadedBarbers && !shopBarbersAreLoading,
            }),
            onClick,
          };
        }}
        pageSize={Number.MAX_SAFE_INTEGER}
      />
    </div>
  );
};

Assignments.propTypes = {
  shops: PropTypes.arrayOf(PropTypes.shape({ id: PropTypes.string, name: PropTypes.string })),
  expandedShopId: PropTypes.string,
  toggleExpandedShop: PropTypes.func.isRequired,
  onRowSelectionChange: PropTypes.func.isRequired,
  onFieldChange: PropTypes.func.isRequired,
  getValue: PropTypes.func.isRequired,
  isSelected: PropTypes.func.isRequired,
  isLoading: PropTypes.func.isRequired,
  roleIdType: PropTypes.shape({ id: PropTypes.string, type: PropTypes.string }).isRequired,
  barbersByShop: PropTypes.object.isRequired,
  isLoadingBarbersByShop: PropTypes.object.isRequired,
};

Assignments.defaultProps = {
  shops: [],
  expandedShopId: null,
};

export default Assignments;
